import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Sun, Moon, Mail, KeyRound, Zap, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientLogin = () => {
  const navigate = useNavigate();
  const { login, loginWithToken, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [mode, setMode] = useState('password'); // 'password' | 'otp' | 'magic'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Password mode
  const [formData, setFormData] = useState({ email: '', password: '' });

  // OTP mode
  const [otpEmail, setOtpEmail] = useState('');
  const [otpStep, setOtpStep] = useState('email');
  const [otpCode, setOtpCode] = useState('');

  // Magic link mode
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);

  React.useEffect(() => {
    if (user && user.role === 'client') {
      navigate('/client/dashboard');
    }
  }, [user, navigate]);

  // ── Password login ──────────────────────────────────────────
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password, 'client');
      toast.success('Welcome back!');
      navigate('/client/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP flow ────────────────────────────────────────────────
  const handleOtpRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/otp/request`, { email: otpEmail, portal: 'client' });
      setOtpStep('code');
      toast.success('Check your email for the 6-digit code');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/otp/verify`, { email: otpEmail, otp: otpCode, portal: 'client' });
      loginWithToken(res.data.token, res.data.user);
      toast.success('Welcome back!');
      navigate('/client/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  // ── Magic link flow ─────────────────────────────────────────
  const handleMagicRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/magic-link/request`, { email: magicEmail, portal: 'client' });
      setMagicSent(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'password', label: 'Password', icon: KeyRound },
    { id: 'otp', label: 'Email OTP', icon: Mail },
    { id: 'magic', label: 'Magic Link', icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7f0] to-[#e8edda] dark:from-[#131a0b] dark:to-[#1e2711] flex items-center justify-center p-4">
      <button
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        className="absolute top-4 right-4 p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all"
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/bda-logo.png" alt="BDA Technologies" className="h-14 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">ContentDesk</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">Client Portal</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl p-8">
          {/* Tab switcher */}
          <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mb-6">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => { setMode(id); setOtpStep('email'); setMagicSent(false); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  mode === id
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Password */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="you@company.com" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required className="h-11" data-testid="email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required className="h-11 pr-10" data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-[#556B2F] hover:bg-[#3d4f22] text-white font-medium" data-testid="submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
              </Button>
              <div className="text-center">
                <Link to="/client/forgot-password"
                  className="text-sm text-[#556B2F] hover:text-[#3d4f22] dark:text-[#6B8E23]"
                  data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            </form>
          )}

          {/* OTP */}
          {mode === 'otp' && (
            <>
              {otpStep === 'email' ? (
                <form onSubmit={handleOtpRequest} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="otp-email">Email</Label>
                    <Input id="otp-email" type="email" placeholder="you@company.com" value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)} required className="h-11" />
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full h-11 bg-[#556B2F] hover:bg-[#3d4f22] text-white font-medium">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Code'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleOtpVerify} className="space-y-5">
                  <div className="text-center mb-2">
                    <p className="text-sm text-slate-500">Code sent to <span className="font-medium text-slate-700 dark:text-slate-300">{otpEmail}</span></p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="otp-code">6-digit code</Label>
                    <Input id="otp-code" type="text" inputMode="numeric" maxLength={6}
                      placeholder="123456" value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      required className="h-11 text-center text-2xl tracking-widest font-mono" />
                  </div>
                  <Button type="submit" disabled={loading || otpCode.length !== 6}
                    className="w-full h-11 bg-[#556B2F] hover:bg-[#3d4f22] text-white font-medium">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Code'}
                  </Button>
                  <button type="button" onClick={() => { setOtpStep('email'); setOtpCode(''); }}
                    className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
                    <ArrowLeft className="w-3.5 h-3.5" /> Use different email
                  </button>
                </form>
              )}
            </>
          )}

          {/* Magic Link */}
          {mode === 'magic' && (
            <>
              {!magicSent ? (
                <form onSubmit={handleMagicRequest} className="space-y-5">
                  <p className="text-sm text-slate-500">We'll email you a secure link — no password needed.</p>
                  <div className="space-y-2">
                    <Label htmlFor="magic-email">Email</Label>
                    <Input id="magic-email" type="email" placeholder="you@company.com" value={magicEmail}
                      onChange={(e) => setMagicEmail(e.target.value)} required className="h-11" />
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full h-11 bg-[#556B2F] hover:bg-[#3d4f22] text-white font-medium">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Magic Link'}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-4 space-y-3">
                  <div className="w-14 h-14 bg-[#d8e5b8] dark:bg-[#556B2F]/15 rounded-full flex items-center justify-center mx-auto">
                    <Mail className="w-7 h-7 text-[#556B2F]" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Check your inbox</h3>
                  <p className="text-sm text-slate-500">
                    Sent a magic link to <span className="font-medium text-slate-700 dark:text-slate-300">{magicEmail}</span>.<br />
                    It expires in 15 minutes.
                  </p>
                  <button type="button" onClick={() => { setMagicSent(false); setMagicEmail(''); }}
                    className="text-sm text-[#556B2F] hover:underline">
                    Use different email
                  </button>
                </div>
              )}
            </>
          )}

          <p className="mt-6 text-center text-sm text-slate-500">
            Don't have an account? Contact your agency for an invite.
          </p>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
